/**
 * useLanguageSuggestion.js
 *
 * Detecta el país del visitante via IP y sugiere cambiar al idioma local
 * mediante un toast de sonner, si el idioma actual de la UI es diferente.
 *
 * Reglas:
 *  - Solo se muestra UNA VEZ por dispositivo (flag en localStorage).
 *  - Si el usuario ya eligió idioma manualmente (startip_lang_manual), NO sugiere.
 *  - Solo idiomas europeos soportados por STARTIP.
 *  - El toast se muestra en el idioma sugerido (mejor UX para el visitante extranjero).
 *  - Falla silenciosamente si la API de geolocalización no responde.
 *  - NUNCA altera la marca STARTIP ni nombres de modelos.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// ── Países europeos → código de idioma soportado ────────────────────────────
const COUNTRY_TO_LANG = {
  // Español
  ES: "es",
  // Inglés
  GB: "en", IE: "en", MT: "en",
  // Francés
  FR: "fr", BE: "fr", LU: "fr", MC: "fr",
  // Alemán
  DE: "de", AT: "de", CH: "de", LI: "de",
  // Italiano
  IT: "it", SM: "it", VA: "it",
  // Portugués
  PT: "pt",
  // Holandés
  NL: "nl",
  // Polaco
  PL: "pl",
  // Rumano
  RO: "ro", MD: "ro",
};

// ── Nombres de países para el mensaje del toast ──────────────────────────────
const COUNTRY_NAMES = {
  GB: "the United Kingdom", IE: "Ireland", MT: "Malta",
  FR: "France", BE: "Belgium", LU: "Luxembourg", MC: "Monaco",
  DE: "Germany", AT: "Austria", CH: "Switzerland", LI: "Liechtenstein",
  IT: "Italy", SM: "San Marino", VA: "Vatican",
  PT: "Portugal",
  NL: "Netherlands",
  PL: "Poland",
  RO: "Romania", MD: "Moldova",
};

// ── Mensajes en el idioma SUGERIDO (no en el idioma actual de la UI) ─────────
const MESSAGES = {
  en: (country) => ({
    title: `Visiting from ${country}?`,
    desc: "Switch to English for a better experience",
    action: "Switch to English",
    success: "Language changed to English",
  }),
  fr: (country) => ({
    title: `Vous visitez depuis ${country} ?`,
    desc: "Passer en Français pour une meilleure expérience",
    action: "Passer en Français",
    success: "Langue changée en Français",
  }),
  de: (country) => ({
    title: `Sie besuchen uns aus ${country}?`,
    desc: "Wechseln Sie zu Deutsch für ein besseres Erlebnis",
    action: "Zu Deutsch wechseln",
    success: "Sprache auf Deutsch geändert",
  }),
  it: (country) => ({
    title: `Stai visitando dalla ${country}?`,
    desc: "Passa all'Italiano per un'esperienza migliore",
    action: "Passa all'Italiano",
    success: "Lingua cambiata in Italiano",
  }),
  pt: (country) => ({
    title: `Visitando de ${country}?`,
    desc: "Mude para Português para uma melhor experiência",
    action: "Mudar para Português",
    success: "Idioma alterado para Português",
  }),
  nl: (country) => ({
    title: `Bezoek je vanuit ${country}?`,
    desc: "Schakel over naar Nederlands voor een betere ervaring",
    action: "Overschakelen naar Nederlands",
    success: "Taal gewijzigd naar Nederlands",
  }),
  pl: (country) => ({
    title: `Odwiedzasz z ${country}?`,
    desc: "Przełącz na Polski, aby uzyskać lepsze doświadczenie",
    action: "Przełącz na Polski",
    success: "Język zmieniony na Polski",
  }),
  ro: (country) => ({
    title: `Vizitezi din ${country}?`,
    desc: "Comutați la Română pentru o experiență mai bună",
    action: "Comutați la Română",
    success: "Limbă schimbată în Română",
  }),
};

// ── Banderas para el icono del toast ────────────────────────────────────────
const LANG_FLAGS = {
  en: "🇬🇧", fr: "🇫🇷", de: "🇩🇪", it: "🇮🇹",
  pt: "🇵🇹", nl: "🇳🇱", pl: "🇵🇱", ro: "🇷🇴",
};

export function useLanguageSuggestion() {
  const { i18n } = useTranslation();

  useEffect(() => {
    // ── Modo demo: ?_lang_demo=FR fuerza el toast (para testing / demos) ─────
    const urlParams = new URLSearchParams(window.location.search);
    const demoCountry = urlParams.get("_lang_demo")?.toUpperCase();
    const isDemo = Boolean(demoCountry);

    // ── Solo mostrar UNA VEZ (ignorar en modo demo) ───────────────────────────
    if (!isDemo && localStorage.getItem("startip_ip_checked")) return;

    // ── No interrumpir si el usuario ya eligió idioma manualmente ────────────
    if (!isDemo && localStorage.getItem("startip_lang_manual")) {
      localStorage.setItem("startip_ip_checked", "true");
      return;
    }

    // ── Marcar como revisado para evitar llamadas duplicadas ─────────────────
    if (!isDemo) localStorage.setItem("startip_ip_checked", "true");

    // ── Esperar 2.5s para no interferir con la carga inicial ─────────────────
    const timer = setTimeout(async () => {
      try {
        let countryCode;

        if (isDemo) {
          // Modo demo: usar el código de país del parámetro URL directamente
          countryCode = demoCountry;
        } else {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 4000);

          // api.country.is — libre, sin límite de rate, HTTPS, retorna {"ip":"...","country":"FR"}
          const res = await fetch("https://api.country.is/", { signal: controller.signal });
          clearTimeout(timeoutId);

          if (!res.ok) return;

          const data = await res.json();
          countryCode = data.country;
        }

        const countryName = COUNTRY_NAMES[countryCode] || countryCode;
        if (!countryCode || !countryName) return;

        // Idioma sugerido según país
        const suggestedLang = COUNTRY_TO_LANG[countryCode];
        if (!suggestedLang) return; // País no europeo o no soportado

        // No sugerir si ya está en ese idioma
        const currentLang = (i18n.language || "es").slice(0, 2);
        if (suggestedLang === currentLang) return;

        const msgs = MESSAGES[suggestedLang];
        if (!msgs) return;

        const { title, desc, action, success } = msgs(countryName);
        const flag = LANG_FLAGS[suggestedLang] || "🌐";

        // ── Toast de sugerencia ────────────────────────────────────────────────
        toast(`${flag} ${title}`, {
          description: desc,
          action: {
            label: action,
            onClick: () => {
              i18n.changeLanguage(suggestedLang);
              localStorage.setItem("startip_lang_manual", "true");
              toast.success(`${flag} ${success}`, { duration: 2500 });
            },
          },
          duration: 10000, // 10 segundos antes de auto-cerrar
          id: "lang-suggestion", // Evitar duplicados
        });
      } catch {
        // Falla silenciosa — geolocalización es una mejora opcional
      }
    }, 2500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
